import { Button, Dropdown, Menu, Modal, Pagination, Select, Space, TableColumnsType, Tabs, Typography } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ContentWrapper } from "../../Components/Layout/ContentWrapper";
import { DataTable } from "../../Components/DataTable/DataTable";
import { ConfirmDeleteModal } from "../../Components/ConfirmDeleteModal";

import { APPROVAL_TABS, APPROVER_GROUPS, findApproverGroup } from "./Constants";
import { approverApi, divisionApi, panelUrl, subsidiaryApi, toArray } from "../../Services";
import { getFormattedDate, getKey } from "../../Utils/CommonUtils";
import { wasUpdated } from "../../Utils/auditFields";
import { usePanelOrgTree } from "../../Hooks/usePanelOrgTree";
import { useNotification } from "../../Hooks/useNotification";
import styles from "./ApprovalManagement.module.scss";
import workOrderStyles from "../WorkOrder/WorkOrder.module.scss";

/**
 * Approval Management — administers the approval routing tree.
 *
 * Three tabs over the same shell: Subsidiary/Division, Cost Center and
 * Approvers. Each row carries an edit/delete action; delete is confirmed
 * through a modal whose copy names the record type being removed.
 *
 * The active tab lives in the URL (`?activeTab=`) like the other list screens,
 * so a tab is linkable and survives refresh.
 */
/** Rows per page on all three tabs — what the pager and the fetch must agree on. */
const RECORDS_PER_PAGE = 10;

/* A record that has never been edited has no "last updated" — the API stamps
   the update fields at creation, so a new row claimed it had been revised the
   moment it was created (see Utils/auditFields). */
const updatedDateOf = (row: any) =>
  wasUpdated(row) ? getFormattedDate(row.updatedOn) || "-" : "-";

const updatedByOf = (row: any) => (wasUpdated(row) ? row.updatedBy || "-" : "-");

/**
 * Column -> API sort field, per tab.
 *
 * The table sorts server side, so the clicked column has to be named the way
 * the endpoint expects rather than the way the UI labels it — "Cost Centre" is
 * `division` to the API. The endpoints sort dates on `createdOn` only, so the
 * Last Updated Date column sorts by creation time (see Utils/sortField).
 * A column missing from the map is left unsorted rather than sent through and
 * rejected.
 */
const SORT_FIELDS: Record<string, Record<string, string>> = {
  [APPROVAL_TABS.SUBSIDIARY]: {
    subsidiary: "subsidiaryName",
    costCentreCount: "divisionCount",
    adminCount: "adminCount",
    createdDate: "createdOn",
    lastUpdatedDate: "createdOn",
    lastUpdatedBy: "updatedBy",
  },
  [APPROVAL_TABS.COST_CENTER]: {
    subsidiary: "subsidiary",
    costCentre: "division",
    adminCount: "adminCount",
    createdDate: "createdOn",
    lastUpdatedDate: "createdOn",
    lastUpdatedBy: "updatedBy",
  },
  [APPROVAL_TABS.APPROVERS]: {
    designation: "designation",
    userName: "userName",
    userEmail: "userEmail",
    subsidiary: "subsidiary",
    costCentre: "division",
    lastUpdatedDate: "createdOn",
    lastUpdatedBy: "updatedBy",
  },
};

export const ApprovalManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("activeTab") || APPROVAL_TABS.SUBSIDIARY;
  const [subsidiaryFilter, setSubsidiaryFilter] = useState<string>("");
  const [costCentreFilter, setCostCentreFilter] = useState<string>("");
  /* These filters narrow tables of panel-contract records, so their options
     have to come from the same configured set rather than the ePOD org tree —
     otherwise the dropdowns offer names that can never match a row. */
  const {
    subsidiaries,
    costCentresFor,
    resolveSubsidiary,
    isLoading: orgLoading,
  } = usePanelOrgTree();
  /** Header click -> server-side sort, mirroring the Work Order list. */
  const handleTableSort = (column: any) => {
    const field = SORT_FIELDS[activeTab]?.[column.dataIndex];
    if (!field) return; // not sortable on this endpoint
    setSortOrder((current) => (orderByName === field && current === "asc" ? "desc" : "asc"));
    setOrderByName(field);
  };

  /** Attaches the sort handler to every column that declares `sorter`. */
  const sortable = (columns: TableColumnsType): TableColumnsType =>
    columns.map((column: any) => ({
      ...column,
      onHeaderCell: (clicked: any) => ({
        onClick: () => clicked.sorter && handleTableSort(clicked),
      }),
    }));

  /* Which designation group the Approvers tab is showing. Kept in the URL
     alongside activeTab so a group is linkable. */
  const approverGroup = searchParams.get("group") || APPROVER_GROUPS[0].key;
  /* The Approvers tab's Subsidiary / Cost Centre filters. Cleared whenever
     the authority sub-tab changes (see resetListState) — they used to be kept
     per authority, so coming back to one showed the filter left on it. */
  const NO_AUTHORITY_FILTER = { subsidiary: "", costCentre: "" };
  const [authorityFilter, setAuthorityFilterState] = useState(NO_AUTHORITY_FILTER);
  const setAuthorityFilter = (patch: Partial<{ subsidiary: string; costCentre: string }>) =>
    setAuthorityFilterState((current) => ({ ...current, ...patch }));
  const [pendingDelete, setPendingDelete] = useState<{ label: string; record: any } | null>(null);
  /** The record type whose delete was refused: it is already used by a work order. */
  const [inUse, setInUse] = useState<string | null>(null);
  const { openNotification } = useNotification();

  const [subsidiaryRows, setSubsidiaryRows] = useState<any[]>([]);
  const [costCentreRows, setCostCentreRows] = useState<any[]>([]);
  const [approverRows, setApproverRows] = useState<any[]>([]);
  const [totals, setTotals] = useState({ subsidiary: 0, costCentre: 0, approver: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [reload, setReload] = useState(0);
  /* Server-side sort. The columns declare `sorter: true` but nothing was
     reading the click, so the arrows moved and the rows never did. */
  /* Ticked rows on the active tab, for "Export selected". */
  /* The active tab's list request, re-run page by page by DataTable's own
     Export — the Manage Catalog pattern, so "All" is every page. */
  const [exportUrl, setExportUrl] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  /* The list endpoints sort on createdOn — see Utils/sortField. */
  const [orderByName, setOrderByName] = useState("createdOn");
  /* Server-side paging, as on the Work Order list. All three endpoints take
     `page` / `size` and none of them were being sent, so every tab fetched
     page 0 at the API's own default size of 20 while the pager underneath was
     decoration — no `current`, no `onChange` — and clicking page 2 refetched
     nothing and re-showed the same rows. */
  const [page, setPage] = useState(0);
  /* Search runs on the server. All three endpoints take `searchText`, and with
     one page of ten rows in hand a client-side filter could only ever look at
     what is already on screen — a record on page 4 was unfindable. */
  const [searchText, setSearchText] = useState("");
  /* Blanks the search box without reaching into DataTable's own state, so a
     tab that is revisited does not show a query it is no longer applying. */
  const [clearSearchInput, setClearSearchInput] = useState(false);

  const runSearch = useMemo(
    () =>
      debounce((value: string) => {
        setPage(0);
        setSearchText(value);
      }, 500),
    []
  );
  useEffect(() => () => runSearch.cancel(), [runSearch]);

  /* The box clears the moment they type; only the request waits for a pause. */
  const handleSearchChange = (value: string) => {
    setClearSearchInput(false);
    runSearch(value);
  };

  /* Page, search and ticked rows belong to the list on screen, so any tab or
     sub-tab change starts them afresh. */
  const resetPagingAndSearch = () => {
    setPage(0);
    runSearch.cancel();
    setSearchText("");
    setClearSearchInput(true);
  };

  /* Any tab or sub-tab change starts the list clean: search, page, every
     filter and the sort go back to their defaults, so coming back to a tab
     shows its default records rather than what was last set there. */
  const resetListState = () => {
    resetPagingAndSearch();
    setSubsidiaryFilter("");
    setCostCentreFilter("");
    setAuthorityFilterState(NO_AUTHORITY_FILTER);
    setSortOrder("desc");
    setOrderByName("createdOn");
  };

  /* Each tab's record as its row reads — shared by the table and its Export.
     `recordId` is what Edit opens; the row key is page and index, which is how
     Export recognises the rows ticked for "Selected". */
  const toSubsidiaryRow = (row: any) => ({
    ...row,
    recordId: row.subsidiaryId,
    subsidiary: row.subsidiaryName,
    /* The API's `divisionCount` comes back 0 even for a subsidiary that
       plainly has cost centres, so it falls back to counting the configured
       ones this screen already has to hand. */
    costCentreCount: row.divisionCount || costCentresFor([row.subsidiaryName]).length || 0,
    adminCount: row.adminCount ?? (row.subsidiaryAdmins?.length ?? 0),
    createdDate: getFormattedDate(row.createdOn) || "-",
    lastUpdatedDate: updatedDateOf(row),
    lastUpdatedBy: updatedByOf(row),
  });
  const toCostCentreRow = (row: any) => ({
    ...row,
    recordId: row.uniqueId,
    // The API's `division` is this UI's "Cost Centre".
    costCentre: row.division,
    subsidiary: row.subsidiary ?? row.subsidiaryId,
    adminCount: row.adminCount ?? (row.divisionAdmins?.length ?? 0),
    createdDate: getFormattedDate(row.createdOn) || "-",
    lastUpdatedDate: updatedDateOf(row),
    lastUpdatedBy: updatedByOf(row),
  });
  const toApproverRow = (row: any) => ({
    ...row,
    recordId: row.uniqueId,
    subsidiary: resolveSubsidiary(row.subsidiary),
    costCentre: row.division,
    lastUpdatedDate: updatedDateOf(row),
    lastUpdatedBy: updatedByOf(row),
  });

  /* Each tab loads only when it is the active one — the three endpoints are
     independent and there is no reason to fetch all of them up front. */
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    const done = () => {
      if (!cancelled) setIsLoading(false);
    };

    if (activeTab === APPROVAL_TABS.SUBSIDIARY) {
      const params = {
        page,
        size: RECORDS_PER_PAGE,
        searchText: searchText || undefined,
        order: sortOrder,
        orderBy: orderByName,
      };
      setExportUrl(panelUrl("/subsidiary/get-subsidiary-list", params));
      subsidiaryApi
        .list(params)
        .then((res) => {
          if (cancelled) return;
          setSubsidiaryRows(
            toArray(res).map((row: any, index: number) => ({ ...toSubsidiaryRow(row), key: getKey(page, index) }))
          );
          setTotals((t) => ({ ...t, subsidiary: res?.totalCount ?? 0 }));
        })
        .catch(() => !cancelled && setSubsidiaryRows([]))
        .finally(done);
    } else if (activeTab === APPROVAL_TABS.COST_CENTER) {
      const params = {
        page,
        size: RECORDS_PER_PAGE,
        searchText: searchText || undefined,
        subsidiary: subsidiaryFilter || undefined,
        order: sortOrder,
        orderBy: orderByName,
      };
      setExportUrl(panelUrl("/division/get-division-list", params));
      divisionApi
        .list(params)
        .then((res) => {
          if (cancelled) return;
          setCostCentreRows(
            toArray(res).map((row: any, index: number) => ({ ...toCostCentreRow(row), key: getKey(page, index) }))
          );
          setTotals((t) => ({ ...t, costCentre: res?.totalCount ?? 0 }));
        })
        .catch(() => !cancelled && setCostCentreRows([]))
        .finally(done);
    } else {
      const params = {
        page,
        size: RECORDS_PER_PAGE,
        searchText: searchText || undefined,
        /* The URL carries our key; the API switches on its own name. */
        authorityGroup: findApproverGroup(approverGroup)?.authorityGroup ?? approverGroup,
        subsidiary: authorityFilter.subsidiary || undefined,
        division: authorityFilter.costCentre || undefined,
        order: sortOrder,
        orderBy: orderByName,
      };
      setExportUrl(panelUrl("/work-order/approver/get-approver-list", params));
      approverApi
        .list(params)
        .then((res) => {
          if (cancelled) return;
          setApproverRows(
            toArray(res).map((row: any, index: number) => ({ ...toApproverRow(row), key: getKey(page, index) }))
          );
          setTotals((t) => ({ ...t, approver: res?.totalCount ?? 0 }));
        })
        .catch(() => !cancelled && setApproverRows([]))
        .finally(done);
    }

    return () => {
      cancelled = true;
    };
    /* `orgLoading` is a dependency because the approver rows resolve their
       subsidiary id through the org tree — mapping them before it lands would
       leave the raw id in the column. */
  }, [
    activeTab,
    approverGroup,
    subsidiaryFilter,
    costCentreFilter,
    authorityFilter.subsidiary,
    authorityFilter.costCentre,
    page,
    searchText,
    reload,
    sortOrder,
    orderByName,
    orgLoading,
  ]);

  /** Delete routes to the endpoint matching whichever tab is active. */
  const confirmDelete = () => {
    const record = pendingDelete?.record;
    if (!record) return;
    const request =
      activeTab === APPROVAL_TABS.COST_CENTER
        ? divisionApi.removeDefault(record.uniqueId)
        : activeTab === APPROVAL_TABS.APPROVERS
          ? approverApi.remove(record.uniqueId)
          : subsidiaryApi.removeDefault(record.subsidiaryId);

    request
      .then(() => {
        /* "removed" read as though something was still pending; the record is
           gone by the time this shows. */
        openNotification({
          title: "Deleted",
          message: `${recordLabel} deleted successfully.`,
          type: "success",
        });
        setReload((n) => n + 1);
      })
      .catch((err: any) =>
        openNotification({ title: "Delete failed", message: err?.message, type: "error" })
      )
      .finally(() => setPendingDelete(null));
  };

  /** Label used in the delete confirmation, per tab. */
  const recordLabel =
    activeTab === APPROVAL_TABS.COST_CENTER
      ? "Cost Centre"
      : activeTab === APPROVAL_TABS.APPROVERS
        ? "Approver"
        : "Subsidiary/Division";

  const rowActions = (record: any, editPath: string) => (
    <Dropdown
      overlay={
        <Menu
          items={[
            {
              key: "edit",
              label: (
                <Space>
                  <Icon
                    icon="akar-icons:edit"
                    className={styles.editIcon}
                  />
                  Edit
                </Space>
              ),
            },
            {
              key: "delete",
              label: (
                <Space>
                  <Icon
                    icon="fluent:delete-48-regular"
                    className={styles.deleteIcon}
                  />
                  <Typography.Text type="danger">Delete</Typography.Text>
                </Space>
              ),
            },
          ]}
          onClick={({ key, domEvent }) => {
            // See WorkOrder.tsx: a portalled menu still bubbles up the React tree.
            domEvent.stopPropagation();
            if (key === "edit") {
              /* The approver edit path already carries `?group=`; a second "?"
                 made the id part of the group value, so the edit screen loaded
                 with no record and the wrong designation group. */
              const separator = editPath.includes("?") ? "&" : "?";
              navigate(`${editPath}${separator}id=${encodeURIComponent(record.recordId)}`);
            } else if (Number(record.appliedCount) > 0) {
              /* `appliedCount` is how many work orders use it — any at all and it cannot go. */
              setInUse(recordLabel);
            } else {
              setPendingDelete({ label: recordLabel, record });
            }
          }}
        />
      }
      trigger={["click"]}
      placement="bottomRight"
    >
      <span onClick={(e) => e.stopPropagation()}>
        <Icon
          icon="bi:three-dots-vertical"
          style={{ cursor: "pointer" }}
        />
      </span>
    </Dropdown>
  );


  const commonTail: TableColumnsType = [
    { title: "Created date", dataIndex: "createdDate", sorter: true },
    { title: "Last Updated Date", dataIndex: "lastUpdatedDate", sorter: true },
    { title: "Last Updated By", dataIndex: "lastUpdatedBy", sorter: true },
  ];

  const subsidiaryColumns: TableColumnsType = [
    { title: "Subsidiary/Division", dataIndex: "subsidiary", sorter: true },
    { title: "No. Of Cost Centre", dataIndex: "costCentreCount", sorter: true },
    { title: "No. Of Admins", dataIndex: "adminCount", sorter: true },
    ...commonTail,
    {
      title: "Action",
      dataIndex: "action",
      width: 90,
      render: (_v: any, record: any) => rowActions(record, "/approval-management/subsidiary/edit"),
    },
  ];

  const costCentreColumns: TableColumnsType = [
    { title: "Subsidiary/Division", dataIndex: "subsidiary", sorter: true },
    { title: "Cost Centre", dataIndex: "costCentre", sorter: true },
    { title: "No. Of Admins", dataIndex: "adminCount", sorter: true },
    ...commonTail,
    {
      title: "Action",
      dataIndex: "action",
      width: 90,
      render: (_v: any, record: any) => rowActions(record, "/approval-management/cost-centre/edit"),
    },
  ];

  const approverColumns: TableColumnsType = [
    { title: "Designation", dataIndex: "designation", sorter: true },
    { title: "User Name", dataIndex: "userName", sorter: true },
    { title: "User Email Id", dataIndex: "userEmail", sorter: true },
    { title: "Subsidiary/Division", dataIndex: "subsidiary", sorter: true },
    { title: "Cost Centre", dataIndex: "costCentre", sorter: true },
    { title: "Last Updated Date", dataIndex: "lastUpdatedDate", sorter: true },
    { title: "Last Updated By", dataIndex: "lastUpdatedBy", sorter: true },
    {
      title: "Action",
      dataIndex: "action",
      width: 90,
      render: (_v: any, record: any) =>
        rowActions(record, `/approval-management/approver/edit?group=${approverGroup}`),
    },
  ];

  /* Subsidiary + Cost Centre filters shared by the Approvers sub-tabs. */
  const approverFilters = (
    <>
      <Select
        showSearch
        optionFilterProp="children"
        style={{ minWidth: 200 }}
        placeholder="Select Subsidiary/Division"
        value={authorityFilter.subsidiary || undefined}
        allowClear={!!authorityFilter.subsidiary}
        getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
        onChange={(value: string) => {
          /* A new filter is a new result set: staying on page 3 would ask for
             a page the narrower list no longer has and show nothing. */
          setPage(0);
          setAuthorityFilter({ subsidiary: value ?? "", costCentre: "" });
        }}
      >
        {subsidiaries.map((name: string, index: number) => (
          <Select.Option
            value={name}
            key={`${index}`}
          >
            {name}
          </Select.Option>
        ))}
      </Select>
      <Select
        showSearch
        optionFilterProp="children"
        style={{ minWidth: 180 }}
        placeholder="Select Cost Centre"
        value={authorityFilter.costCentre || undefined}
        /* Selectable on its own: with no subsidiary chosen it offers every
           configured cost centre rather than sitting greyed out. */
        allowClear={!!authorityFilter.costCentre}
        getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
        onChange={(value: string) => {
          setPage(0);
          setAuthorityFilter({ costCentre: value ?? "" });
        }}
      >
        {costCentresFor([authorityFilter.subsidiary].filter(Boolean)).map((name: string, index: number) => (
          <Select.Option
            value={name}
            key={`${index}`}
          >
            {name}
          </Select.Option>
        ))}
      </Select>
    </>
  );

  /* What Export writes for the tab on screen — only that tab's table is
     mounted (destroyInactiveTabPane), so one setting serves all three. */
  const exportFor =
    activeTab === APPROVAL_TABS.SUBSIDIARY
      ? { name: "Subsidiary Division", mapper: toSubsidiaryRow }
      : activeTab === APPROVAL_TABS.COST_CENTER
      ? { name: "Cost Centre", mapper: toCostCentreRow }
      : { name: "Approvers", mapper: toApproverRow };

  const panel = (
    heading: string,
    columns: TableColumnsType,
    rows: any[],
    searchPlaceholder: string,
    cta: React.ReactNode,
    total: number
  ) => (
    <>
      {heading && <Typography.Title level={4}>{heading}</Typography.Title>}
      <DataTable
        columns={sortable(columns)}
        tableData={rows}
        isLoading={isLoading}
        hideExport={false}
        exportApiUrl={exportUrl}
        exportFileName={exportFor.name}
        exportRowMapper={exportFor.mapper}
        excludeExportCols={["Action"]}
        additionalCTA={
          <Space
            wrap
            style={{ width: "100%", justifyContent: "flex-end" }}
          >
            {cta}
          </Space>
        }
        searchPlaceholderText={searchPlaceholder}
        onSearchChange={handleSearchChange}
        isServerSearchApi
        clearStringSearch={clearSearchInput}
        totalRecords={total}
        isPagination={true}
        recordsPerPage={RECORDS_PER_PAGE}
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={page + 1}
          pageSize={RECORDS_PER_PAGE}
          total={total}
          onChange={(nextPage) => setPage(nextPage - 1)}
          showSizeChanger={false}
        />
      </div>
    </>
  );

  const items = [
    {
      key: APPROVAL_TABS.SUBSIDIARY,
      label: "Subsidiary/Division",
      children: panel(
        "Subsidiary/Division",
        subsidiaryColumns,
        subsidiaryRows,
        "Search Subsidiary/Division number...",
        null,
        totals.subsidiary
      ),
    },
    {
      key: APPROVAL_TABS.COST_CENTER,
      label: "Cost Center",
      children: panel(
        "Cost Center",
        costCentreColumns,
        costCentreRows,
        "Search Cost Center",
        <>
          <Select
            showSearch
            optionFilterProp="children"
            style={{ minWidth: 220 }}
            placeholder="Select Subsidiary/Division"
            value={subsidiaryFilter || undefined}
            allowClear={!!subsidiaryFilter}
            getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
            onChange={(value: string) => {
              setPage(0);
              setSubsidiaryFilter(value ?? "");
            }}
          >
            {subsidiaries.map((name: string, index: number) => (
              <Select.Option
                value={name}
                key={`${index}`}
              >
                {name}
              </Select.Option>
            ))}
          </Select>
        </>,
        totals.costCentre
      ),
    },
    {
      key: APPROVAL_TABS.APPROVERS,
      label: "Approvers",
      children: (
        <div className={`${workOrderStyles.pillTabs} ${workOrderStyles.subPillTabs}`}>
          {/* Unmounted when left: each table keeps its own ticked rows, so a
              section still mounted in the background came back with the
              selection made there. The filters live up here and survive. */}
          <Tabs
            destroyInactiveTabPane
            items={APPROVER_GROUPS.map((group) => ({
              key: group.key,
              label: group.label,
              children: panel(
                "",
                approverColumns,
                approverRows,
                "Search Approvers",
                approverFilters,
                totals.approver
              ),
            }))}
            activeKey={approverGroup}
            onChange={(key) => {
              resetListState();
              setSearchParams({ activeTab: APPROVAL_TABS.APPROVERS, group: key });
            }}
          />
        </div>
      ),
    },
  ];

  /** "Add" targets whichever record type the active tab administers. */
  const addPath =
    activeTab === APPROVAL_TABS.COST_CENTER
      ? "/approval-management/cost-centre/create"
      : activeTab === APPROVAL_TABS.APPROVERS
        ? `/approval-management/approver/create?group=${approverGroup}`
        : "/approval-management/subsidiary/create";

  return (
    <ContentWrapper
      title="Approval Management"
      actionItems={
        <Button
          type="primary"
          onClick={() => navigate(addPath)}
        >
          <Space>
            <Icon
              icon="akar-icons:plus"
              inline
            />
            Add
          </Space>
        </Button>
      }
    >
      <div className={workOrderStyles.pillTabs}>
        <Tabs
          destroyInactiveTabPane
          items={items}
          activeKey={activeTab}
          onChange={(key) => {
          resetListState();
          setSearchParams({ activeTab: key });
        }}
        />
      </div>

      <ConfirmDeleteModal
        open={!!pendingDelete}
        message={`Are you sure you want to delete the ${pendingDelete?.label ?? ""} details?`}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
      <Modal
        open={!!inUse}
        title={`Delete ${inUse ?? ""}`}
        centered
        closable={false}
        maskClosable={false}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setInUse(null)}
          >
            Ok
          </Button>,
        ]}
      >
        <Typography.Text>
          This {inUse?.toLowerCase()} has already been used to create a work order, so it cannot be deleted.
        </Typography.Text>
      </Modal>
    </ContentWrapper>
  );
};
